import type { DecimalSeparator } from './locale-format';

/** Separador de miles complementario al decimal regional ('.' → ',' y viceversa). */
export function thousandsSeparator(dec: DecimalSeparator): string {
  return dec === ',' ? '.' : ',';
}

/**
 * Convierte lo que el usuario teclea/pega (con el separador de miles y decimal
 * regionales) a la forma canónica numérica ("1234.5") que consumen `parseFloat`
 * y el backend. Descarta todo carácter que no sea dígito, signo inicial o el
 * separador decimal regional. Conserva un "." final ("12.") para poder seguir tecleando.
 */
export function parseNumberInput(
  display: string,
  dec: DecimalSeparator,
  opts: { allowNegative?: boolean; maxDecimals?: number } = {},
): string {
  const { allowNegative = false, maxDecimals } = opts;
  let neg = false;
  let intPart = '';
  let decPart = '';
  let seenDec = false;
  for (const ch of display) {
    if (ch >= '0' && ch <= '9') {
      if (seenDec) decPart += ch; else intPart += ch;
    } else if (ch === dec && !seenDec) {
      seenDec = true;
    } else if (ch === '-' && allowNegative && intPart === '' && !seenDec) {
      neg = true;
    }
  }
  if (maxDecimals !== undefined) decPart = decPart.slice(0, maxDecimals);
  intPart = intPart.replace(/^0+(?=\d)/, '');
  if (seenDec && intPart === '') intPart = '0';
  return `${neg ? '-' : ''}${intPart}${seenDec ? `.${decPart}` : ''}`;
}

/** Forma canónica ("1234.5") → texto con separadores regionales ("1,234.5" / "1.234,5"). */
export function formatNumberInput(canonical: string, dec: DecimalSeparator): string {
  if (!canonical) return '';
  const neg = canonical.startsWith('-');
  const body = neg ? canonical.slice(1) : canonical;
  const [intPart, decPart] = body.split('.');
  const grouped = (intPart ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator(dec));
  const hasDec = body.includes('.');
  return `${neg ? '-' : ''}${grouped}${hasDec ? `${dec}${decPart ?? ''}` : ''}`;
}

/** Texto canónico → número (undefined si está vacío o no es numérico). */
export function canonicalToNumber(canonical: string): number | undefined {
  if (!canonical || canonical === '-') return undefined;
  const n = Number(canonical);
  return Number.isFinite(n) ? n : undefined;
}
